import type { User, OrganizationWithRole, Domain, DMARCReport, DMARCRecord } from "@dmarc/shared";

const API_BASE = import.meta.env.VITE_API_URL || "/api";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new ApiError(response.status, body.error || "An error occurred", body.details);
  }
  return response.json();
}

// Response types
interface AuthResponse {
  user: User;
  isAdmin: boolean;
  organizations: OrganizationWithRole[];
}

interface OrganizationMember {
  id: string;
  email: string;
  name: string;
  role: string;
}

interface OrganizationWithMembers {
  id: string;
  name: string;
  slug: string;
  plan: string;
  members: OrganizationMember[];
  currentUserRole: string;
}

interface RegisterResponse {
  user: User;
  organization: OrganizationWithRole;
}

interface Summary {
  totalReports: number;
  totalMessages: number;
  passedMessages: number;
  failedMessages: number;
  passRate: number;
}

interface TrendData {
  date: string;
  total: number;
  compliant: number;
  forwarded: number;
  nonCompliant: number;
  threat: number;
}

interface SourceData {
  sourceIp: string;
  country: string | null;
  asnOrg: string | null;
  totalCount: number;
  passCount: number;
  failCount: number;
  passRate: number;
}

interface Plan {
  id: string;
  name: string;
  domains: number;
  messagesPerMonth: number;
  price: number;
}

interface Subscription {
  plan: string;
  planName: string;
  status: string | null;
  currentPeriodEnd: string | null;
  domainLimit: number;
  messageLimit: number;
  monthlyMessageCount: number;
  usageResetAt: string;
}

interface AdminStats {
  users: {
    total: number;
    today: number;
    thisWeek: number;
    thisMonth: number;
  };
  organizations: {
    total: number;
    byPlan: Record<string, number>;
  };
  domains: {
    total: number;
    verified: number;
  };
  reports: {
    total: number;
    thisMonth: number;
  };
}

interface RecentRegistration {
  id: string;
  email: string;
  name: string;
  createdAt: string;
}

interface SubscriptionInfo {
  id: string;
  name: string;
  plan: string;
  stripeSubscriptionId: string | null;
  createdAt: string;
}

interface DailyStats {
  date: string;
  count: number;
}

interface ApiUsageStats {
  period: string;
  total: number;
  avgResponseTimeMs: number;
  byEndpoint: Array<{ endpoint: string; count: number }>;
  byIp: Array<{ ipAddress: string; count: number }>;
  hourly: Array<{ hour: string; count: number }>;
}

interface ApiAbuser {
  ipAddress: string;
  count: number;
  endpoints: string[];
  userAgent: string | null;
}

interface ApiUsageRecord {
  id: string;
  endpoint: string;
  method: string;
  ipAddress: string;
  userAgent: string | null;
  userId: string | null;
  statusCode: number | null;
  responseTimeMs: number | null;
  requestBody: string | null;
  createdAt: string;
}

interface OverLimitOrganization {
  id: string;
  name: string;
  plan: string;
  monthlyMessageCount: number;
  messageLimit: number;
  percentUsed: number;
  overLimitAt: string;
  daysOverLimit: number;
  ownerEmail: string | null;
}

interface OverLimitResponse {
  count: number;
  organizations: OverLimitOrganization[];
}

interface Conversion {
  id: string;
  previousPlan: string;
  newPlan: string;
  conversionType: string;
  trigger: string;
  monthlyValue: number | null;
  createdAt: string;
  organization: {
    id: string;
    name: string;
  };
}

interface ConversionsResponse {
  conversions: Conversion[];
}

interface ConversionStats {
  period: string;
  totals: Record<string, { count: number; mrrChange: number }>;
  byTransition: Array<{ previousPlan: string; newPlan: string; count: number }>;
  daily: Array<{ date: string; upgrades: number; downgrades: number; churns: number }>;
}

interface SourceLookupResult {
  ip: string;
  ptr: string | null;
  asn: number | null;
  asnOrg: string | null;
  country: string | null;
  matchedService: {
    id: string;
    name: string;
    slug: string;
    category: string;
    spfInclude: string | null;
    dkimSetupUrl: string | null;
    alignmentFix: "dkim" | "spf" | "both" | null;
    logoUrl: string | null;
    websiteUrl: string | null;
  } | null;
  matchConfidence: "high" | "medium" | "low" | null;
  recommendation: string | null;
}

interface DigestStats {
  totalEmails: number;
  passRate: number;
  passCount: number;
  failCount: number;
  previousWeekTotal: number;
  previousWeekPassRate: number;
  trend: "up" | "down" | "stable";
  trendPercent: number;
}

interface ActionItem {
  type: "failing_service" | "unknown_sender" | "policy_recommendation";
  priority: "high" | "medium" | "low";
  title: string;
  description: string;
  sourceIp?: string;
  serviceName?: string;
  spfInclude?: string;
  dkimSetupUrl?: string;
  emailCount: number;
  domain?: string;
}

interface NewSender {
  sourceIp: string;
  serviceName?: string;
  emailCount: number;
  passRate: number;
}

interface DomainHealth {
  domain: string;
  passRate: number;
  totalEmails: number;
  status: "healthy" | "warning" | "critical";
}

interface WeeklyDigest {
  organizationName: string;
  stats: DigestStats;
  actionItems: ActionItem[];
  newSenders: NewSender[];
  domainHealth: DomainHealth[];
  hasData: boolean;
}

interface DismissedActionItem {
  id: string;
  actionType: string;
  actionKey: string;
  status: "resolved" | "dismissed";
  dismissReason: string | null;
  createdAt: string;
  domainId: string | null;
  domain: string | null;
  resolvedByName: string | null;
}

// Aggregate report types
interface AggregateSummary {
  compliant: number;
  forwarded: number;
  nonCompliant: number;
  threat: number;
  total: number;
}

interface SendingDomain {
  domain: string;
  volume: number;
  spfPassRate: number;
  dkimPassRate: number;
}

interface SendingSource {
  sourceIp: string;
  serviceName: string | null;
  serviceCategory: string | null;
  volume: number;
  spfPassRate: number;
  dkimPassRate: number;
}

interface AggregateData {
  summary: AggregateSummary;
  sendingDomains: SendingDomain[];
  sendingSources: SendingSource[];
}

interface FailureRecord {
  id: string;
  sourceIp: string;
  domain: string;
  messageCount: number;
  spfResult: string;
  dkimResult: string;
  spfDomain: string | null;
  dkimDomain: string | null;
  spfAligned: boolean;
  dkimAligned: boolean;
  policyDomain: string;
  disposition: string;
  serviceName: string | null;
  serviceCategory: string | null;
  spfInclude: string | null;
  dkimSetupUrl: string | null;
  alignmentFix: "dkim" | "spf" | "both" | null;
  asnOrg: string | null;
  category: "non_compliant" | "threat";
  reportDate: string;
}

interface FailuresResponse {
  records: FailureRecord[];
  total: number;
}

// Unified sending source record (includes compliant + failures)
interface SendingSourceRecord {
  id: string;
  sourceIp: string;
  domain: string;
  messageCount: number;
  dmarcPass: boolean;
  spfResult: string;
  dkimResult: string;
  spfDomain: string | null;
  dkimDomain: string | null;
  spfAligned: boolean;
  dkimAligned: boolean;
  policyDomain: string;
  disposition: string;
  serviceName: string | null;
  serviceCategory: string | null;
  spfInclude: string | null;
  dkimSetupUrl: string | null;
  alignmentFix: "dkim" | "spf" | "both" | null;
  asnOrg: string | null;
  status: "compliant" | "forwarded" | "non_compliant" | "threat";
  reportDate: string;
}

interface SendingSourcesResponse {
  records: SendingSourceRecord[];
  total: number;
}

export type { Summary, TrendData, SourceData, Plan, Subscription, OrganizationMember, OrganizationWithMembers, AdminStats, RecentRegistration, SubscriptionInfo, DailyStats, ApiUsageStats, ApiAbuser, ApiUsageRecord, OverLimitOrganization, OverLimitResponse, Conversion, ConversionsResponse, ConversionStats, SourceLookupResult, WeeklyDigest, ActionItem, NewSender, DomainHealth, DismissedActionItem, AggregateData, AggregateSummary, SendingDomain, SendingSource, FailureRecord, FailuresResponse, SendingSourceRecord, SendingSourcesResponse };

export const api = {
  // Auth
  auth: {
    register: (data: { email: string; password: string; name: string }): Promise<RegisterResponse> =>
      fetch(`${API_BASE}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      }).then(handleResponse<RegisterResponse>),

    login: (data: { email: string; password: string }): Promise<AuthResponse> =>
      fetch(`${API_BASE}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      }).then(handleResponse<AuthResponse>),

    logout: (): Promise<{ success: boolean }> =>
      fetch(`${API_BASE}/auth/logout`, {
        method: "POST",
        credentials: "include",
      }).then(handleResponse<{ success: boolean }>),

    me: (): Promise<AuthResponse> =>
      fetch(`${API_BASE}/auth/me`, {
        credentials: "include",
      }).then(handleResponse<AuthResponse>),

    forgotPassword: (email: string): Promise<{ success: boolean }> =>
      fetch(`${API_BASE}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      }).then(handleResponse<{ success: boolean }>),

    resetPassword: (token: string, password: string): Promise<{ success: boolean }> =>
      fetch(`${API_BASE}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      }).then(handleResponse<{ success: boolean }>),

    verifyEmail: (token: string): Promise<{ success: boolean }> =>
      fetch(`${API_BASE}/auth/verify-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      }).then(handleResponse<{ success: boolean }>),

    resendVerification: (): Promise<{ success: boolean }> =>
      fetch(`${API_BASE}/auth/resend-verification`, {
        method: "POST",
        credentials: "include",
      }).then(handleResponse<{ success: boolean }>),

    updateProfile: (data: { name: string }): Promise<{ user: User }> =>
      fetch(`${API_BASE}/auth/profile`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      }).then(handleResponse<{ user: User }>),

    changePassword: (currentPassword: string, newPassword: string): Promise<{ success: boolean }> =>
      fetch(`${API_BASE}/auth/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ currentPassword, newPassword }),
      }).then(handleResponse<{ success: boolean }>),
  },

  // Organizations
  organizations: {
    list: (): Promise<OrganizationWithRole[]> =>
      fetch(`${API_BASE}/organizations`, {
        credentials: "include",
      }).then(handleResponse<OrganizationWithRole[]>),

    get: (id: string): Promise<OrganizationWithRole> =>
      fetch(`${API_BASE}/organizations/${id}`, {
        credentials: "include",
      }).then(handleResponse<OrganizationWithRole>),

    create: (data: { name: string }): Promise<OrganizationWithRole> =>
      fetch(`${API_BASE}/organizations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      }).then(handleResponse<OrganizationWithRole>),

    update: (id: string, data: { name?: string }): Promise<OrganizationWithRole> =>
      fetch(`${API_BASE}/organizations/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      }).then(handleResponse<OrganizationWithRole>),

    delete: (id: string): Promise<{ success: boolean }> =>
      fetch(`${API_BASE}/organizations/${id}`, {
        method: "DELETE",
        credentials: "include",
      }).then(handleResponse<{ success: boolean }>),

    invite: (id: string, data: { email: string; role?: string }): Promise<{ success: boolean }> =>
      fetch(`${API_BASE}/organizations/${id}/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      }).then(handleResponse<{ success: boolean }>),

    getWithMembers: (id: string): Promise<OrganizationWithMembers> =>
      fetch(`${API_BASE}/organizations/${id}`, {
        credentials: "include",
      }).then(handleResponse<OrganizationWithMembers>),

    removeMember: (orgId: string, userId: string): Promise<{ success: boolean }> =>
      fetch(`${API_BASE}/organizations/${orgId}/members/${userId}`, {
        method: "DELETE",
        credentials: "include",
      }).then(handleResponse<{ success: boolean }>),

    updateMemberRole: (orgId: string, userId: string, role: string): Promise<{ success: boolean }> =>
      fetch(`${API_BASE}/organizations/${orgId}/members/${userId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ role }),
      }).then(handleResponse<{ success: boolean }>),

    transferOwnership: (orgId: string, newOwnerId: string): Promise<{ success: boolean }> =>
      fetch(`${API_BASE}/organizations/${orgId}/transfer-ownership`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ newOwnerId }),
      }).then(handleResponse<{ success: boolean }>),
  },

  // Domains
  domains: {
    list: (organizationId: string): Promise<Domain[]> =>
      fetch(`${API_BASE}/domains?organizationId=${organizationId}`, {
        credentials: "include",
      }).then(handleResponse<Domain[]>),

    get: (id: string): Promise<Domain> =>
      fetch(`${API_BASE}/domains/${id}`, {
        credentials: "include",
      }).then(handleResponse<Domain>),

    create: (data: { domain: string; organizationId: string }): Promise<Domain> =>
      fetch(`${API_BASE}/domains`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      }).then(handleResponse<Domain>),

    verify: (id: string): Promise<{ verified: boolean; message: string }> =>
      fetch(`${API_BASE}/domains/${id}/verify`, {
        method: "POST",
        credentials: "include",
      }).then(handleResponse<{ verified: boolean; message: string }>),

    dnsStatus: (id: string): Promise<{ dmarc: string | null; spf: string | null }> =>
      fetch(`${API_BASE}/domains/${id}/dns-status`, {
        credentials: "include",
      }).then(handleResponse<{ dmarc: string | null; spf: string | null }>),

    dnsProvider: (id: string): Promise<{
      provider: {
        id: string;
        name: string;
        links: {
          spf?: string;
          dkim?: string;
          dmarc?: string;
          securityHeaders?: string;
          addTxtRecord?: string;
          addCnameRecord?: string;
        };
      };
      nameservers: string[];
    }> =>
      fetch(`${API_BASE}/domains/${id}/dns-provider`, {
        credentials: "include",
      }).then(handleResponse<{
        provider: {
          id: string;
          name: string;
          links: {
            spf?: string;
            dkim?: string;
            dmarc?: string;
            securityHeaders?: string;
            addTxtRecord?: string;
            addCnameRecord?: string;
          };
        };
        nameservers: string[];
      }>),

    updatePolicy: (id: string, data: { configuredPolicy?: string; configuredPct?: number; configuredSp?: string | null }): Promise<Domain> =>
      fetch(`${API_BASE}/domains/${id}/policy`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      }).then(handleResponse<Domain>),

    delete: (id: string): Promise<{ success: boolean }> =>
      fetch(`${API_BASE}/domains/${id}`, {
        method: "DELETE",
        credentials: "include",
      }).then(handleResponse<{ success: boolean }>),
  },

  // Reports
  reports: {
    list: (params: { organizationId: string; domainId?: string; limit?: number; offset?: number }): Promise<DMARCReport[]> => {
      const searchParams = new URLSearchParams({ organizationId: params.organizationId });
      if (params.domainId) searchParams.set("domainId", params.domainId);
      if (params.limit) searchParams.set("limit", String(params.limit));
      if (params.offset) searchParams.set("offset", String(params.offset));
      return fetch(`${API_BASE}/reports?${searchParams}`, {
        credentials: "include",
      }).then(handleResponse<DMARCReport[]>);
    },

    get: (id: string): Promise<DMARCReport & { domain: string; records: DMARCRecord[] }> =>
      fetch(`${API_BASE}/reports/${id}`, {
        credentials: "include",
      }).then(handleResponse<DMARCReport & { domain: string; records: DMARCRecord[] }>),

    summary: (params: { organizationId: string; domainId?: string; days?: number }): Promise<Summary> => {
      const searchParams = new URLSearchParams({ organizationId: params.organizationId });
      if (params.domainId) searchParams.set("domainId", params.domainId);
      if (params.days) searchParams.set("days", String(params.days));
      return fetch(`${API_BASE}/reports/summary?${searchParams}`, {
        credentials: "include",
      }).then(handleResponse<Summary>);
    },

    sources: (params: { organizationId: string; domainId?: string; days?: number; limit?: number }): Promise<SourceData[]> => {
      const searchParams = new URLSearchParams({ organizationId: params.organizationId });
      if (params.domainId) searchParams.set("domainId", params.domainId);
      if (params.days) searchParams.set("days", String(params.days));
      if (params.limit) searchParams.set("limit", String(params.limit));
      return fetch(`${API_BASE}/reports/sources?${searchParams}`, {
        credentials: "include",
      }).then(handleResponse<SourceData[]>);
    },

    trends: (params: { organizationId: string; domainId?: string; days?: number }): Promise<TrendData[]> => {
      const searchParams = new URLSearchParams({ organizationId: params.organizationId });
      if (params.domainId) searchParams.set("domainId", params.domainId);
      if (params.days) searchParams.set("days", String(params.days));
      return fetch(`${API_BASE}/reports/trends?${searchParams}`, {
        credentials: "include",
      }).then(handleResponse<TrendData[]>);
    },

    upload: (xml: string): Promise<{ success: boolean; reportId?: string }> =>
      fetch(`${API_BASE}/webhooks/dmarc-report/upload`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ xml }),
      }).then(handleResponse<{ success: boolean; reportId?: string }>),

    lookupSource: (ip: string): Promise<SourceLookupResult> =>
      fetch(`${API_BASE}/reports/source-lookup/${encodeURIComponent(ip)}`, {
        credentials: "include",
      }).then(handleResponse<SourceLookupResult>),

    lookupSources: (ips: string[]): Promise<Record<string, SourceLookupResult>> =>
      fetch(`${API_BASE}/reports/source-lookup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ips }),
      }).then(handleResponse<Record<string, SourceLookupResult>>),

    digest: (organizationId: string, domainId?: string): Promise<WeeklyDigest> => {
      const params = new URLSearchParams({ organizationId });
      if (domainId) params.append("domainId", domainId);
      return fetch(`${API_BASE}/reports/digest?${params}`, {
        credentials: "include",
      }).then(handleResponse<WeeklyDigest>);
    },

    // Action item management
    resolveActionItem: (data: {
      organizationId: string;
      domainId?: string;
      actionType: string;
      actionKey: string;
      serviceName?: string;
      spfInclude?: string;
      domain?: string;
    }): Promise<{ verified: boolean; message: string }> =>
      fetch(`${API_BASE}/reports/action-items/resolve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      }).then(handleResponse<{ verified: boolean; message: string }>),

    dismissActionItem: (data: {
      organizationId: string;
      domainId?: string;
      actionType: string;
      actionKey: string;
      reason: "spoofing" | "not_my_service" | "other";
    }): Promise<{ success: boolean }> =>
      fetch(`${API_BASE}/reports/action-items/dismiss`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      }).then(handleResponse<{ success: boolean }>),

    getDismissedActionItems: (organizationId: string): Promise<DismissedActionItem[]> =>
      fetch(`${API_BASE}/reports/action-items/dismissed?organizationId=${organizationId}`, {
        credentials: "include",
      }).then(handleResponse<DismissedActionItem[]>),

    restoreActionItem: (id: string, organizationId: string): Promise<{ success: boolean }> =>
      fetch(`${API_BASE}/reports/action-items/dismissed/${id}?organizationId=${organizationId}`, {
        method: "DELETE",
        credentials: "include",
      }).then(handleResponse<{ success: boolean }>),

    // Aggregate and failures
    aggregate: (params: { organizationId: string; domainId?: string; days?: number }): Promise<AggregateData> => {
      const searchParams = new URLSearchParams({ organizationId: params.organizationId });
      if (params.domainId) searchParams.set("domainId", params.domainId);
      if (params.days) searchParams.set("days", String(params.days));
      return fetch(`${API_BASE}/reports/aggregate?${searchParams}`, {
        credentials: "include",
      }).then(handleResponse<AggregateData>);
    },

    failures: (params: { organizationId: string; domainId?: string; days?: number; category?: "non_compliant" | "threat"; limit?: number; offset?: number }): Promise<FailuresResponse> => {
      const searchParams = new URLSearchParams({ organizationId: params.organizationId });
      if (params.domainId) searchParams.set("domainId", params.domainId);
      if (params.days) searchParams.set("days", String(params.days));
      if (params.category) searchParams.set("category", params.category);
      if (params.limit) searchParams.set("limit", String(params.limit));
      if (params.offset) searchParams.set("offset", String(params.offset));
      return fetch(`${API_BASE}/reports/failures?${searchParams}`, {
        credentials: "include",
      }).then(handleResponse<FailuresResponse>);
    },

    sendingSources: (params: { organizationId: string; domainId?: string; days?: number; status?: string; limit?: number; offset?: number }): Promise<SendingSourcesResponse> => {
      const searchParams = new URLSearchParams({ organizationId: params.organizationId });
      if (params.domainId) searchParams.set("domainId", params.domainId);
      if (params.days) searchParams.set("days", String(params.days));
      if (params.status) searchParams.set("status", params.status);
      if (params.limit) searchParams.set("limit", String(params.limit));
      if (params.offset) searchParams.set("offset", String(params.offset));
      return fetch(`${API_BASE}/reports/sending-sources?${searchParams}`, {
        credentials: "include",
      }).then(handleResponse<SendingSourcesResponse>);
    },

    exportCsv: async (params: { organizationId: string; domainId?: string; days?: number; type: "aggregate" | "failures" }): Promise<Blob> => {
      const searchParams = new URLSearchParams({
        organizationId: params.organizationId,
        type: params.type,
      });
      if (params.domainId) searchParams.set("domainId", params.domainId);
      if (params.days) searchParams.set("days", String(params.days));
      const response = await fetch(`${API_BASE}/reports/export/csv?${searchParams}`, {
        credentials: "include",
      });
      if (!response.ok) {
        throw new ApiError(response.status, "Failed to export CSV");
      }
      return response.blob();
    },
  },

  // DNS Tools
  tools: {
    validateSPF: (domain: string) =>
      fetch(`${API_BASE}/tools/spf/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ domain }),
      }).then(handleResponse<{ valid: boolean; record: string | null; errors: string[]; warnings: string[]; lookupCount: number }>),

    generateSPF: (options: Record<string, unknown>) =>
      fetch(`${API_BASE}/tools/spf/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(options),
      }).then(handleResponse<{ record: string }>),

    validateDKIM: (domain: string, selector: string) =>
      fetch(`${API_BASE}/tools/dkim/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ domain, selector }),
      }).then(handleResponse<{ valid: boolean; record: string | null; errors: string[]; keyInfo?: Record<string, unknown> }>),

    discoverDKIM: (domain: string) =>
      fetch(`${API_BASE}/tools/dkim/discover`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ domain }),
      }).then(handleResponse<{ selectors: string[] }>),

    validateDMARC: (domain: string) =>
      fetch(`${API_BASE}/tools/dmarc/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ domain }),
      }).then(handleResponse<{ valid: boolean; record: string | null; errors: string[]; warnings: string[]; parsed?: Record<string, unknown> }>),

    generateDMARC: (options: Record<string, unknown>) =>
      fetch(`${API_BASE}/tools/dmarc/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(options),
      }).then(handleResponse<{ record: string }>),

    checkAll: (domain: string) =>
      fetch(`${API_BASE}/tools/check-all`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ domain }),
      }).then(handleResponse<{ spf: Record<string, unknown>; dkim: Record<string, unknown>; dmarc: Record<string, unknown> }>),

    dnsLookup: (domain: string) =>
      fetch(`${API_BASE}/tools/dns-lookup/${domain}`, {
        credentials: "include",
      }).then(handleResponse<Record<string, unknown>>),

    securityScan: (domain: string, forceRefresh?: boolean) =>
      fetch(`${API_BASE}/tools/security-scan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ domain, forceRefresh }),
      }).then(handleResponse<{
        domain: string;
        ipAddress: string | null;
        overallScore: number;
        grade: string;
        ssl: {
          valid: boolean;
          issuer: string | null;
          expiresAt: string | null;
          daysUntilExpiry: number | null;
          protocol: string | null;
          errors: string[];
          warnings: string[];
          score: number;
        };
        headers: {
          score: number;
          headers: {
            name: string;
            present: boolean;
            value: string | null;
            status: "pass" | "warning" | "fail";
            importance: "critical" | "high" | "medium" | "low";
            description: string;
            recommendation: string | null;
          }[];
          errors: string[];
        };
        blacklist: {
          clean: boolean;
          checked: string[];
          detectedOn: string[];
          errors: string[];
        };
        cached: boolean;
        scannedAt: string;
      }>),
  },

  // Billing
  billing: {
    getPlans: (): Promise<Plan[]> =>
      fetch(`${API_BASE}/billing/plans`, {
        credentials: "include",
      }).then(handleResponse<Plan[]>),

    getSubscription: (organizationId: string): Promise<Subscription> =>
      fetch(`${API_BASE}/billing/subscription?organizationId=${organizationId}`, {
        credentials: "include",
      }).then(handleResponse<Subscription>),

    createCheckout: (organizationId: string, planId: string): Promise<{ url: string }> =>
      fetch(`${API_BASE}/billing/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ organizationId, planId }),
      }).then(handleResponse<{ url: string }>),

    createPortal: (organizationId: string): Promise<{ url: string }> =>
      fetch(`${API_BASE}/billing/portal`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ organizationId }),
      }).then(handleResponse<{ url: string }>),
  },

  // Admin
  admin: {
    getStats: (): Promise<AdminStats> =>
      fetch(`${API_BASE}/admin/stats`, {
        credentials: "include",
      }).then(handleResponse<AdminStats>),

    getRegistrations: (limit?: number): Promise<RecentRegistration[]> =>
      fetch(`${API_BASE}/admin/registrations${limit ? `?limit=${limit}` : ""}`, {
        credentials: "include",
      }).then(handleResponse<RecentRegistration[]>),

    getSubscriptions: (): Promise<SubscriptionInfo[]> =>
      fetch(`${API_BASE}/admin/subscriptions`, {
        credentials: "include",
      }).then(handleResponse<SubscriptionInfo[]>),

    getDailyStats: (days?: number): Promise<DailyStats[]> =>
      fetch(`${API_BASE}/admin/stats/daily${days ? `?days=${days}` : ""}`, {
        credentials: "include",
      }).then(handleResponse<DailyStats[]>),

    getApiUsage: (hours?: number): Promise<ApiUsageStats> =>
      fetch(`${API_BASE}/admin/api-usage${hours ? `?hours=${hours}` : ""}`, {
        credentials: "include",
      }).then(handleResponse<ApiUsageStats>),

    getApiAbusers: (hours?: number, threshold?: number): Promise<{ period: string; threshold: number; abusers: ApiAbuser[] }> =>
      fetch(`${API_BASE}/admin/api-usage/abusers?hours=${hours || 1}&threshold=${threshold || 50}`, {
        credentials: "include",
      }).then(handleResponse<{ period: string; threshold: number; abusers: ApiAbuser[] }>),

    getRecentApiUsage: (limit?: number): Promise<ApiUsageRecord[]> =>
      fetch(`${API_BASE}/admin/api-usage/recent${limit ? `?limit=${limit}` : ""}`, {
        credentials: "include",
      }).then(handleResponse<ApiUsageRecord[]>),

    getOverLimitOrganizations: (): Promise<OverLimitResponse> =>
      fetch(`${API_BASE}/admin/over-limit`, {
        credentials: "include",
      }).then(handleResponse<OverLimitResponse>),

    searchOrganizations: (query: string): Promise<{ organizations: Array<{
      id: string;
      name: string;
      slug: string;
      plan: string;
      domainLimit: number;
      messageLimit: number;
      createdAt: string;
    }> }> =>
      fetch(`${API_BASE}/admin/organizations/search?q=${encodeURIComponent(query)}`, {
        credentials: "include",
      }).then(handleResponse<{ organizations: Array<{
        id: string;
        name: string;
        slug: string;
        plan: string;
        domainLimit: number;
        messageLimit: number;
        createdAt: string;
      }> }>),

    setOrganizationPlan: (orgId: string, plan: string): Promise<{ success: boolean; organization: { id: string; name: string; plan: string; domainLimit: number; messageLimit: number } }> =>
      fetch(`${API_BASE}/admin/organizations/${orgId}/set-plan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ plan }),
      }).then(handleResponse<{ success: boolean; organization: { id: string; name: string; plan: string; domainLimit: number; messageLimit: number } }>),

    getConversions: (params?: { limit?: number; days?: number }): Promise<ConversionsResponse> => {
      const searchParams = new URLSearchParams();
      if (params?.limit) searchParams.set("limit", String(params.limit));
      if (params?.days) searchParams.set("days", String(params.days));
      return fetch(`${API_BASE}/admin/conversions?${searchParams}`, {
        credentials: "include",
      }).then(handleResponse<ConversionsResponse>);
    },

    getConversionStats: (days?: number): Promise<ConversionStats> =>
      fetch(`${API_BASE}/admin/conversions/stats${days ? `?days=${days}` : ""}`, {
        credentials: "include",
      }).then(handleResponse<ConversionStats>),

    getNewsletterSubscribers: (): Promise<{ subscribers: Array<{ id: string; email: string; source: string; createdAt: string }>; total: number }> =>
      fetch(`${API_BASE}/newsletter/admin/subscribers`, {
        credentials: "include",
      }).then(handleResponse<{ subscribers: Array<{ id: string; email: string; source: string; createdAt: string }>; total: number }>),

    getNewsletterStats: (): Promise<{ total: number }> =>
      fetch(`${API_BASE}/newsletter/admin/stats`, {
        credentials: "include",
      }).then(handleResponse<{ total: number }>),
  },

  newsletter: {
    subscribe: (email: string, source?: string): Promise<{ success: boolean; message: string }> =>
      fetch(`${API_BASE}/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, source }),
      }).then(handleResponse<{ success: boolean; message: string }>),
  },
};
